import BaseWidget from "common/widgets/base/BaseWidget";
import locale from "common/managers/locale";
import "common/bindings/field";
import PasswordFieldDecorator from "common/widgets/PromptList/PasswordFieldDecorator";
import stringUtil from "common/helper/stringUtil";

var EVENT_NS = ".TextInput";

const TextInput = BaseWidget(kendo.ui.Widget).extend({
    options: {
        name: "TextInput",

        //Should the control be enabled?
        enabled: true,

        //Should the control be readonly?
        readonly: false,

        // locale entry for placeholder text
        placeholderEntry: null,

        //Placeholder text
        placeholder: null,

        //If provided, the text to use in the aria-label property
        labelKey: null,

        // if provided, specifies upper or lower case
        characterCase: null,

        // Max length of input
        maxlength: null,

        wrapperClass: null,

        // whether to conceal input characters
        conceal: false
    },

    events: [
        /** Fired when the a blur event occurs and the value of the text input has changed since focus. */
        "change",
        /** Fired whenever a key is released. */
        "keyup",
        /**
         * Fired whenever the input displayed in the box is changed.  Do not use this instead of change unless absoutley necessary.
         * It will fire on every keystroke,paste operation, or programattc change of the control.
         */
        "inputChange"
    ],

    //The value of the widget
    _value: "",

    //A user-friendly display version of the value of the widget
    _formatted: "",

    //Is the widget currently focused
    _isFocused: false,

    //The value of the widget when "focus" was last called on it
    _valueOnFocus: "",

    init: function() {
        kendo.ui.Widget.fn.init.apply(this, arguments);
        var id = this.element.attr("id");
        if (!id) {
            console.error("TextInput widget requires an id");
        }
        if (!this.element.attr("name")) {
            this.element.attr("name", id);
        }

        if (this.options.placeholder) {
            this.element.attr("placeholder", this.options.placeholder);
        } else if (this.options.placeholderEntry) {
            this.element.attr("placeholder", locale.get(this.options.placeholderEntry));
        }

        this.characterCase(this.options.characterCase);

        this.options.maxlength = this.options.maxlength || this.element.attr("maxlength");
        this.element.removeAttr("maxlength");

        if (!this.element.attr("type")) {
            this.element.attr("type", "text");
        }

        //Setup wrapper
        this.element.wrap($("<span>").addClass("textInputWrapper"));
        this.wrapper = this.element.parent();
        if(this.options.wrapperClass) {
            this.wrapper.addClass(this.options.wrapperClass);
        }

        //Edit wrapper
        this.element.wrap($("<span>").addClass("editable"));
        this.edit = this.element.parent();

        //Setup readonly span
        this.read = $("<span>").addClass("display");
        this.wrapper.append(this.read);

        //custom validation location
        this.wrapper.append($("<span class='k-invalid-msg'>").attr("data-for", this.element.attr("name")));

        //Handle keyup event
        this._keyup = $.proxy(this._keyup, this);
        this.element.on("keyup" + EVENT_NS, this._keyup);

        this._keydown = $.proxy(this._keydown, this);
        this.element.on("keydown" + EVENT_NS, this._keydown);

        //Handle input event
        this._input = $.proxy(this._input, this);
        this.element.on("input" + EVENT_NS, this._input);

        //Handle focus/blur
        this._focus = $.proxy(this._focus, this);
        this._blur = $.proxy(this._blur, this);
        this.element.on("focus" + EVENT_NS, this._focus);
        this.element.on("blur" + EVENT_NS, this._blur);

        //Set initial state
        this.enable(this.options.enabled);
        this.readonly(this.options.readonly);

        if (this.options.labelKey) {
            this.element.attr("aria-label", locale.get(this.options.labelKey));
        }

        if(this.options.conceal) {
            PasswordFieldDecorator.decorate(this.element);
        }
    },

    _toggleFocus: function(focus) {
        this._isFocused = !!focus;
        this._updateDisplay();
    },

    _focus: function() {
        if(this._isFocused) {
            return;
        }
        this._valueOnFocus = this._value;
        this._toggleFocus(true);
        this.trigger("focus");
    },

    _blur: function() {
        this._toggleFocus(false);
        this._change();
        this.trigger("blur");
    },

    _change() {
        if(this._valueOnFocus !== this._value) {
            this._valueOnFocus = this._value;
            this.trigger("change");
        }
    },

    _input: function() {
        var val = this.element.val();
        if (this.isPresentable(val)) {
            if (this._storeValue(val)) {
                this.trigger("inputChange");
            }
        } else {
            this._updateDisplay();
        }
        delete this._lastCaret;
    },

    _keydown: function(e) {
        if(e.keyCode === kendo.keys.ENTER) {
            this._change();
        }
        this._lastCaret = kendo.caret(this.element);
    },

    _storeValue: function(value) {
        var parsed = this.parseValue(value);
        if (this._value !== parsed) {
            this._value = parsed;
            this._formatted = this.formatValue(this._value);
            return true;
        } else {
            return false;
        }

    },

    _updateDisplay: function() {
        if (this._isFocused) {
            this.element.val(this._value);
            if (this._lastCaret) {
                kendo.caret(this.element, this._lastCaret[0], this._lastCaret[1]);
            }
        } else {
            this.element.val(this._formatted);
        }
    },

    parseValue: function(value) {
        if (value === "" || value === null || value === undefined) {
            return "";
        } else {
            return value;
        }
    },

    formatValue: function(value) {
        return value;
    },

    isPresentable: function(val) {
        return !this.options.maxlength || stringUtil.length(val) <= this.options.maxlength;
    },

    destroy: function() {
        delete this.read;
        delete this.wrapper;
        delete this.edit;
        this.element.off(EVENT_NS);
        kendo.ui.Widget.fn.destroy.call(this);
    },

    enable: function(enabled) {
        this.options.enabled = enabled;
        if (this.options.enabled) {
            this.element.removeAttr("disabled")
        } else {
            this._toggleFocus(false);
            this.element.attr("disabled", true);
        }
    },

    /**
     * Readonly setter.
     */
    readonly: function(readonly) {
        var to = readonly ? true : false;
        this.options.readonly = to;
        //value may have changed
        this._sync();

        this.wrapper.toggleClass("readonly", to);
        this.wrapper.toggleClass("notReadonly", !to);

        if (to) {
            this.element.attr("readonly", to);
        } else {
            this._toggleFocus(false);
            this.element.removeAttr("readonly");
        }
    },

    _keyup: function() {
        this.trigger("keyup");
    },

    _sync: function() {
        this.read.text(this._formatted);
    },

    /**
     * Value getter/setter.
     * @param v value to set
     * @returns {*} value from getter
     */
    value: function(v) {
        if (v === undefined) {
            return this._value;
        } else if (this._storeValue(v)) {
            this._updateDisplay();
            this.trigger("inputChange");
            this._sync();
        }
    },

    input: function() {
        return this.value.apply(this, arguments);
    },

    focus: function() {
        this.element.focus();
        var len = this.element[0].value ? this.element[0].value.length : 0;
        try {
            this.element[0].setSelectionRange(len, len);
        } catch (e) {
            // ignore; setSelectionRange only supports when type=text but focus will work for others
        }
    },

    /**
     * Changes character case of the input value
     * @param characterCase NORMAL, UPPER, LOWER
     */
    characterCase: function(characterCase) {
        if (characterCase) {
            var style = "";
            if (characterCase === "UPPER") {
                style = "uppercase";
            } else if (characterCase === "LOWER") {
                style = "lowercase";
            } else {
                characterCase = "NORMAL";
            }
            this.options.characterCase = characterCase;
            this.element.css("text-transform", style);
        }
    }
});
kendo.ui.plugin(TextInput);
export default TextInput;
